import { useHotelSlot } from "@b2bportal/core-hotels";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getImageUrlsArray } from "@b2bportal/core-utilities";
import { toStarRating } from "@b2bportal/core-utilities";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ImageCarousel,
  Tags,
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
} from "@tenant/hotels";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { TripAdvisorRating } from "../TripAdvisorRating";
import defaultStyles from "./HotelCard.module.scss";
import { Rewards } from "@/components/Rewards";
import { getLodgingAddress } from "@/utils/lodging";
import { calculatePointsEarnedForLodging } from "@/utils/points";
export type HotelCardProps = PlatformComponentProps & {
  hotel: Lodging;
  dateRange: DateRange;
  rooms?: number;
  onHover?: (hotelId: string | undefined) => void;
};

export const HotelCard = ({
  className,
  hotel,
  onHover,
  dateRange,
  rooms,
}: HotelCardProps) => {
  const styles = useHotelsStyles(CoreHotelsComponent.HotelCard, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.HotelCard);
  const { t } = useI18nContext();
  const emitHotelSwipedCarousel = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelSwipedCarousel
  );

  const onNavigateToSlide = () => {
    emitHotelSwipedCarousel({ properties: { hotelId: hotel.lodging.id } });
  };

  const HotelSlot = useHotelSlot();
  return (
    <div
      className={clsx(block, className)}
      onMouseOver={() => onHover?.(hotel.lodging.id)}
      onMouseOut={() => onHover?.(undefined)}
      onFocus={() => onHover?.(hotel.lodging.id)}
      onBlur={() => onHover?.(undefined)}
    >
      <div className={cn("carousel-container")}>
        <ImageCarousel
          imageUrlsArray={getImageUrlsArray(hotel.lodging.media)}
          onNavigateToNextSlide={onNavigateToSlide}
          onNavigateToPrevSlide={onNavigateToSlide}
        />
      </div>
      <div className={cn("rewards-container")}>
        <Rewards amount={calculatePointsEarnedForLodging(hotel)} />
      </div>
      <div className={cn("rating-container")}>
        <TripAdvisorRating
          isMinimal
          tripAdvisorReviews={hotel.lodging.tripAdvisorReviews}
          className={cn("tripadvisor-rating")}
        />
        <span className={cn("star-rating")}>
          •{" "}
          {t("core-hotels.availabilityPage.results.hotelCard.stars", {
            count: toStarRating(hotel.lodging.starRating),
          })}
        </span>
      </div>
      <div className={cn("title-container")}>
        <h2 className={cn("title")}>{hotel.lodging.name}</h2>
      </div>
      <div className={cn("location-container")}>
        <Icon iconName={IconNameEnum.mapPin} className={cn("location-icon")} />{" "}
        <span>{getLodgingAddress(hotel.lodging)}</span>
      </div>
      <div className={cn("tags-container")}>
        <Tags hotel={hotel} className={cn("tags")} />
      </div>
      <div className={cn("features-container")}>
        {hotel.isFreeCancel && (
          <div className={cn("free-cancellation")}>
            {t(
              "core-hotels.availabilityPage.results.hotelCard.features.freeCancellation"
            )}
          </div>
        )}
      </div>
      <div className={cn("price-container")}>
        <HotelSlot.HotelCardPrice
          hotel={hotel}
          dateRange={dateRange}
          rooms={rooms}
        />
      </div>
    </div>
  );
};
